type AgentAllowlistChangedDetail = {
  enabled: boolean;
  prefixes: string[];
};

type AgentBlockedDetail = {
  op: 'load' | 'save' | 'delete' | 'list' | 'exists' | 'createDirectory';
  path: string;
  reason: string;
};

const STORAGE_PREFIXES = 'agent.allowlistPrefixes.v1';
const STORAGE_ENABLED = 'agent.guardEnabled.v1';

let cachedEnabled: boolean | null = null;
let cachedPrefixes: string[] | null = null;

function readEnabled(): boolean {
  if (cachedEnabled !== null) return cachedEnabled;
  try {
    cachedEnabled = localStorage.getItem(STORAGE_ENABLED) === 'true';
  } catch {
    cachedEnabled = false;
  }
  return cachedEnabled;
}

function readPrefixes(): string[] {
  if (cachedPrefixes) return cachedPrefixes;
  try {
    const raw = localStorage.getItem(STORAGE_PREFIXES);
    if (!raw) {
      cachedPrefixes = [];
      return cachedPrefixes;
    }
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      cachedPrefixes = parsed.filter((p) => typeof p === 'string' && p.trim().length > 0);
      return cachedPrefixes;
    }
  } catch {
    // ignore
  }
  cachedPrefixes = [];
  return cachedPrefixes;
}

function writeEnabled(v: boolean) {
  cachedEnabled = v;
  try {
    localStorage.setItem(STORAGE_ENABLED, v ? 'true' : 'false');
  } catch {
    // ignore
  }
}

function writePrefixes(prefixes: string[]) {
  cachedPrefixes = prefixes;
  try {
    localStorage.setItem(STORAGE_PREFIXES, JSON.stringify(prefixes));
  } catch {
    // ignore
  }
}

function emitAllowlistChanged() {
  const detail: AgentAllowlistChangedDetail = {
    enabled: readEnabled(),
    prefixes: readPrefixes(),
  };
  window.dispatchEvent(new CustomEvent<AgentAllowlistChangedDetail>('agent:allowlist-changed', { detail }));
}

export function isAgentGuardEnabled(): boolean {
  return readEnabled();
}

export function enableAgentGuard() {
  writeEnabled(true);
  emitAllowlistChanged();
}

export function disableAgentGuard() {
  writeEnabled(false);
  emitAllowlistChanged();
}

export function getAllowlistPrefixes(): string[] {
  return readPrefixes();
}

export function setAllowlistPrefixes(prefixes: string[]) {
  const clean = Array.from(new Set(prefixes.map((p) => normalizeWinPath(p)).filter(Boolean)));
  writePrefixes(clean);
  emitAllowlistChanged();
}

export function addAllowlistPrefix(prefix: string) {
  const p = normalizeWinPath(prefix);
  if (!p) return;
  const next = Array.from(new Set([...readPrefixes(), p]));
  writePrefixes(next);
  emitAllowlistChanged();
}

export function removeAllowlistPrefix(prefix: string) {
  const p = normalizeWinPath(prefix);
  const next = readPrefixes().filter((x) => x !== p);
  writePrefixes(next);
  emitAllowlistChanged();
}

export function clearAllowlistPrefixes() {
  writePrefixes([]);
  emitAllowlistChanged();
}

export function normalizeWinPath(path: string): string {
  if (!path) return '';
  return String(path)
    .trim()
    .replace(/\//g, '\\')
    .replace(/\\\\+/g, '\\')
    .replace(/\\+$/g, '');
}

export function isAgentOperationAllowed(path: string): boolean {
  if (!readEnabled()) return true;

  const p = normalizeWinPath(path);
  const prefixes = readPrefixes();
  if (prefixes.length === 0) return false;

  return prefixes.some((pref) => {
    if (!pref) return false;
    if (p === pref) return true;
    return p.startsWith(pref + '\\');
  });
}

export function dispatchAgentBlocked(detail: AgentBlockedDetail) {
  window.dispatchEvent(new CustomEvent<AgentBlockedDetail>('agent:blocked', { detail }));
}

export type { AgentAllowlistChangedDetail, AgentBlockedDetail };
